import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

const ComplaintDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [complaint, setComplaint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState("");
  const [ministerRemarks, setMinisterRemarks] = useState("");
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentDescription, setAttachmentDescription] = useState("");

  const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5000";

  useEffect(() => {
    const fetchComplaint = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_URL}/api/complaints/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setComplaint(response.data.data);
        setStatus(response.data.data.status);
        setMinisterRemarks(response.data.data.ministerRemarks || "");
      } catch (error) {
        console.error("Error fetching complaint:", error);
        toast.error("Failed to load complaint details");
        navigate("/minister-dashboard");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchComplaint();
  }, [id, API_URL, navigate]);

  const downloadAttachment = (file) => {
    try {
      if (file.url && file.url.startsWith("data:")) {
        const base64Data = file.url.split(",")[1];
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
          type: file.fileType || "application/octet-stream",
        });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = file.originalName || file.filename || "download";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => {
          URL.revokeObjectURL(link.href);
        }, 100);

        toast.success("Downloading file...");
      } else {
        window.open(file.url, "_blank");
      }
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Failed to download file");
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      e.target.value = "";
      return;
    }

    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/jpg",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only PDF, Word, and Image files are allowed");
      e.target.value = "";
      return;
    }

    setAttachmentFile(file);
    toast.success(`File "${file.name}" selected`);
  };

  const handleUpdateStatus = async () => {
    try {
      setUpdating(true);
      const token = localStorage.getItem("token");

      let attachmentData = null;

      if (attachmentFile) {
        setUploading(true);
        const reader = new FileReader();
        const fileData = await new Promise((resolve) => {
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(attachmentFile);
        });

        attachmentData = {
          filename: attachmentFile.name,
          originalName: attachmentFile.name,
          url: fileData,
          fileType: attachmentFile.type,
          fileSize: attachmentFile.size,
          description: attachmentDescription || "Resolution document",
        };
        setUploading(false);
      }

      await axios.put(
        `${API_URL}/api/complaints/${id}/status`,
        {
          status,
          ministerRemarks,
          attachment: attachmentData,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      toast.success("Complaint updated successfully");

      const response = await axios.get(`${API_URL}/api/complaints/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setComplaint(response.data.data);

      setAttachmentFile(null);
      setAttachmentDescription("");
      document.getElementById("attachmentInput").value = "";
    } catch (error) {
      console.error("Error updating complaint:", error);
      toast.error(
        error.response?.data?.message || "Failed to update complaint",
      );
    } finally {
      setUpdating(false);
      setUploading(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: {
        color: "#f39c12",
        bg: "#fff3cd",
        label: "Pending Review",
        borderColor: "#f39c12",
      },
      approved: {
        color: "#27ae60",
        bg: "#d4edda",
        label: "Approved",
        borderColor: "#27ae60",
      },
      rejected: {
        color: "#e74c3c",
        bg: "#f8d7da",
        label: "Rejected",
        borderColor: "#e74c3c",
      },
      resolved: {
        color: "#2ecc71",
        bg: "#d4edda",
        label: "Resolved",
        borderColor: "#2ecc71",
      },
    };
    return configs[status?.toLowerCase()] || configs.pending;
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-PK", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const removeAttachment = () => {
    setAttachmentFile(null);
    setAttachmentDescription("");
    document.getElementById("attachmentInput").value = "";
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            "linear-gradient(135deg, #01411C 0%, #1a6b3a 50%, #01411C 100%)",
        }}
      >
        <div style={{ textAlign: "center", color: "white" }}>
          <div
            style={{
              width: "60px",
              height: "60px",
              border: "4px solid rgba(255,255,255,0.3)",
              borderTop: "4px solid #F9A826",
              // borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
              margin: "0 auto 20px",
            }}
          />
          <p style={{ fontSize: "18px", opacity: 0.8 }}>
            Loading complaint details...
          </p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!complaint) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            "linear-gradient(135deg, #01411C 0%, #1a6b3a 50%, #01411C 100%)",
        }}
      >
        <div
          style={{
            background: "white",
            padding: "40px",
            // borderRadius: "16px",
            textAlign: "center",
            maxWidth: "400px",
          }}
        >
          <h2 style={{ color: "#01411C", marginTop: "10px" }}>
            Complaint Not Found
          </h2>
          <p style={{ color: "#666" }}>
            The complaint you're looking for doesn't exist or has been removed.
          </p>
          <Link
            to="/minister-dashboard"
            style={{
              display: "inline-block",
              marginTop: "20px",
              padding: "12px 30px",
              background: "#01411C",
              color: "white",
              textDecoration: "none",
              // borderRadius: "8px",
              fontWeight: "600",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 15px rgba(1,65,28,0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
          >
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(complaint.status);

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #01411C 0%, #1a6b3a 50%, #01411C 100%)",
        padding: "clamp(16px, 4vw, 40px)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "fixed",
          top: "-200px",
          right: "-200px",
          width: "400px",
          height: "400px",
          // borderRadius: "50%",
          background: "rgba(249,168,38,0.05)",
          animation: "float 20s ease-in-out infinite",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "fixed",
          bottom: "-200px",
          left: "-200px",
          width: "500px",
          height: "500px",
          // borderRadius: "50%",
          background: "rgba(249,168,38,0.03)",
          animation: "float 25s ease-in-out infinite reverse",
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div
          style={{
            background: "rgba(255,255,255,0.08)",
            backdropFilter: "blur(20px)",
            // borderRadius: "20px",
            padding: "clamp(20px, 3vw, 30px)",
            marginBottom: "clamp(20px, 3vw, 30px)",
            border: "1px solid rgba(255,255,255,0.1)",
            animation: "fadeInDown 0.6s ease-out",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "15px",
            }}
          >
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "5px",
                }}
              >
                <h1
                  style={{
                    color: "white",
                    fontSize: "clamp(22px, 3.5vw, 32px)",
                    margin: 0,
                    fontWeight: "700",
                  }}
                >
                  Complaint Details
                </h1>
              </div>
              <Link
                to="/minister-dashboard"
                style={{
                  color: "#F9A826",
                  textDecoration: "none",
                  fontSize: "clamp(14px, 1.2vw, 16px)",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "5px",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "translateX(-5px)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateX(0)";
                }}
              >
                Back to Dashboard
              </Link>
            </div>
            <div
              style={{
                display: "flex",
                gap: "10px",
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  background: statusConfig.bg,
                  padding: "8px 16px",
                  // borderRadius: "50px",
                  border: `2px solid ${statusConfig.color}`,
                }}
              >
                <span
                  style={{
                    color: statusConfig.color,
                    fontWeight: "700",
                    fontSize: "clamp(13px, 1.1vw, 15px)",
                    textTransform: "capitalize",
                  }}
                >
                  {statusConfig.label}
                </span>
              </div>
              <div
                style={{
                  background: "rgba(255,255,255,0.15)",
                  padding: "8px 16px",
                  // borderRadius: "50px",
                  border: "1px solid rgba(255,255,255,0.2)",
                }}
              >
                <span
                  style={{
                    color: "white",
                    fontWeight: "600",
                    fontSize: "clamp(12px, 1vw, 14px)",
                    fontFamily: "monospace",
                  }}
                >
                  {complaint.trackingId || "N/A"}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr",
            gap: "clamp(20px, 3vw, 30px)",
            animation: "fadeInUp 0.8s ease-out",
          }}
        >
          <div
            style={{
              background: "white",
              // borderRadius: "20px",
              padding: "clamp(24px, 3vw, 35px)",
              boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
              transition: "all 0.3s ease",
            }}
          >
            <h2
              style={{
                color: "#01411C",
                fontSize: "clamp(20px, 2.5vw, 26px)",
                marginBottom: "20px",
                borderBottom: "3px solid #F9A826",
                paddingBottom: "12px",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                textAlign: "justify",
              }}
            >
              {complaint.subject || "Complaint"}
            </h2>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "16px",
                marginBottom: "24px",
              }}
            >
              {[
                { label: "Citizen Name", value: complaint.citizenName },
                { label: "Email", value: complaint.email },
                { label: "Phone", value: complaint.phone },
                { label: "CNIC", value: complaint.cnic },
                { label: "Category", value: complaint.category },
                { label: "District", value: complaint.district },
                { label: "Address", value: complaint.address, full: true },
                { label: "Submitted", value: formatDate(complaint.createdAt) },
                {
                  label: "Last Updated",
                  value: formatDate(complaint.updatedAt),
                },
              ].map((item, index) => (
                <div
                  key={index}
                  style={{
                    gridColumn: item.full ? "1 / -1" : "auto",
                    background: "#f8faf8",
                    padding: "12px 16px",
                    // borderRadius: "12px",
                    border: "1px solid #e8ece8",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 12px rgba(0,0,0,0.05)";
                    e.currentTarget.style.borderColor = "#F9A826";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                    e.currentTarget.style.borderColor = "#e8ece8";
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "4px",
                    }}
                  >
                    <strong
                      style={{
                        fontSize: "clamp(11px, 0.9vw, 13px)",
                        color: "#666",
                      }}
                    >
                      {item.label}
                    </strong>
                  </div>
                  <p
                    style={{
                      margin: "4px 0 0 0",
                      fontSize: "clamp(14px, 1.1vw, 16px)",
                      color: "#1a1a2e",
                      fontWeight: "500",
                      wordBreak: "break-word",
                      textAlign: "justify",
                    }}
                  >
                    {item.value || "N/A"}
                  </p>
                </div>
              ))}
            </div>

            <div style={{ marginBottom: "24px" }}>
              <h4
                style={{
                  color: "#01411C",
                  fontSize: "clamp(15px, 1.2vw, 17px)",
                  marginBottom: "10px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  textAlign: "justify",
                }}
              >
                Description
              </h4>
              <div
                style={{
                  background: "#f8faf8",
                  padding: "clamp(16px, 2vw, 20px)",
                  // borderRadius: "12px",
                  border: "1px solid #e8ece8",
                  minHeight: "80px",
                  textAlign: "justify",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    lineHeight: "1.8",
                    fontSize: "clamp(14px, 1.1vw, 16px)",
                    color: "#333",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    textAlign: "justify",
                  }}
                >
                  {complaint.description || "No description provided"}
                </p>
              </div>
            </div>

            {complaint.ministerRemarks && (
              <div
                style={{
                  background: "linear-gradient(135deg, #fff8e1, #fff3cd)",
                  padding: "clamp(16px, 2vw, 20px)",
                  // borderRadius: "12px",
                  borderLeft: "4px solid #f39c12",
                  marginBottom: "16px",
                }}
              >
                <h4
                  style={{
                    color: "#856404",
                    fontSize: "clamp(14px, 1.1vw, 16px)",
                    marginBottom: "8px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  Minister Remarks
                </h4>
                <p
                  style={{
                    margin: 0,
                    color: "#856404",
                    fontSize: "clamp(14px, 1.1vw, 16px)",
                    lineHeight: "1.6",
                    textAlign: "justify",
                  }}
                >
                  {complaint.ministerRemarks}
                </p>
              </div>
            )}

            {complaint.attachments && complaint.attachments.length > 0 && (
              <div
                style={{
                  background: "linear-gradient(135deg, #e8f5e9, #c8e6c9)",
                  padding: "clamp(16px, 2vw, 20px)",
                  // borderRadius: "12px",
                  borderLeft: "4px solid #27ae60",
                  marginBottom: "16px",
                }}
              >
                <h4
                  style={{
                    color: "#155724",
                    fontSize: "clamp(14px, 1.1vw, 16px)",
                    marginBottom: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  Attachments ({complaint.attachments.length})
                </h4>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "10px",
                  }}
                >
                  {complaint.attachments.map((file, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "10px 16px",
                        background: "white",
                        // borderRadius: "8px",
                        border: "1px solid #c8e6c9",
                        transition: "all 0.3s ease",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                        flexWrap: "wrap",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#f0f7f0";
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow =
                          "0 4px 15px rgba(0,0,0,0.1)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "white";
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow =
                          "0 2px 8px rgba(0,0,0,0.05)";
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: "600", fontSize: "14px" }}>
                          {file.originalName || file.filename}
                        </div>
                        {file.description && (
                          <div style={{ fontSize: "12px", color: "#666" }}>
                            {file.description}
                          </div>
                        )}
                        <div style={{ fontSize: "11px", color: "#888" }}>
                          {(file.fileSize / 1024).toFixed(1)} KB •{" "}
                          {formatDate(file.uploadedAt)}
                        </div>
                      </div>
                      <button
                        onClick={() => downloadAttachment(file)}
                        style={{
                          padding: "4px 12px",
                          background: "#01411C",
                          color: "white",
                          border: "none",
                          // borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "12px",
                          fontWeight: "600",
                          transition: "all 0.3s ease",
                          marginLeft: "auto",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#1a6b3a";
                          e.currentTarget.style.transform = "scale(1.05)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "#01411C";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      >
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {complaint.resolvedDate && (
              <div
                style={{
                  background: "linear-gradient(135deg, #e8f5e9, #c8e6c9)",
                  padding: "clamp(12px, 1.5vw, 16px)",
                  // borderRadius: "12px",
                  borderLeft: "4px solid #27ae60",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <div>
                  <strong style={{ color: "#155724" }}>Resolved:</strong>
                  <span style={{ color: "#155724", marginLeft: "8px" }}>
                    {formatDate(complaint.resolvedDate)}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div>
            <div
              style={{
                background: "white",
                // borderRadius: "20px",
                padding: "clamp(20px, 2.5vw, 28px)",
                boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
                position: "sticky",
                top: "clamp(80px, 12vh, 100px)",
                transition: "all 0.3s ease",
              }}
            >
              <h4
                style={{
                  color: "#01411C",
                  marginBottom: "20px",
                  paddingBottom: "12px",
                  borderBottom: "2px solid #F9A826",
                  fontSize: "clamp(17px, 1.4vw, 19px)",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                Actions
              </h4>

              <div style={{ marginBottom: "18px" }}>
                <label
                  style={{
                    fontWeight: "600",
                    display: "block",
                    marginBottom: "8px",
                    color: "#333",
                    fontSize: "clamp(13px, 1vw, 14px)",
                  }}
                >
                  Update Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    // borderRadius: "10px",
                    border: "2px solid #e0e0e0",
                    fontSize: "clamp(14px, 1.1vw, 15px)",
                    background: "white",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    outline: "none",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#F9A826";
                    e.target.style.boxShadow = "0 0 0 4px rgba(249,168,38,0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e0e0e0";
                    e.target.style.boxShadow = "none";
                  }}
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="resolved">Resolved</option>
                </select>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    fontWeight: "600",
                    display: "block",
                    marginBottom: "8px",
                    color: "#333",
                    fontSize: "clamp(13px, 1vw, 14px)",
                  }}
                >
                  Minister Remarks
                </label>
                <textarea
                  value={ministerRemarks}
                  onChange={(e) => setMinisterRemarks(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    // borderRadius: "10px",
                    border: "2px solid #e0e0e0",
                    fontSize: "clamp(14px, 1.1vw, 15px)",
                    minHeight: "100px",
                    resize: "vertical",
                    fontFamily: "inherit",
                    transition: "all 0.3s ease",
                    outline: "none",
                  }}
                  placeholder="Add your remarks here..."
                  onFocus={(e) => {
                    e.target.style.borderColor = "#F9A826";
                    e.target.style.boxShadow = "0 0 0 4px rgba(249,168,38,0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e0e0e0";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    fontWeight: "600",
                    display: "block",
                    marginBottom: "8px",
                    color: "#333",
                    fontSize: "clamp(13px, 1vw, 14px)",
                  }}
                >
                  Attach File (Optional)
                </label>
                <input
                  type="file"
                  id="attachmentInput"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={handleFileUpload}
                  style={{
                    width: "100%",
                    padding: "10px",
                    // borderRadius: "10px",
                    border: "2px dashed #e0e0e0",
                    fontSize: "clamp(13px, 1vw, 14px)",
                    cursor: "pointer",
                    background: "#f8faf8",
                    transition: "all 0.3s ease",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#F9A826";
                    e.target.style.background = "#fff";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e0e0e0";
                    e.target.style.background = "#f8faf8";
                  }}
                />
                <p
                  style={{
                    fontSize: "11px",
                    color: "#999",
                    marginTop: "5px",
                  }}
                >
                  Supported: PDF, Word, Images (Max 5MB)
                </p>

                {attachmentFile && (
                  <div
                    style={{
                      marginTop: "10px",
                      padding: "10px 14px",
                      background: "#e8f5e9",
                      // borderRadius: "8px",
                      border: "1px solid #c8e6c9",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "8px",
                      animation: "fadeInUp 0.3s ease-out",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: "500", fontSize: "14px" }}>
                          {attachmentFile.name}
                        </div>
                        <div style={{ fontSize: "11px", color: "#666" }}>
                          {(attachmentFile.size / 1024).toFixed(1)} KB
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={removeAttachment}
                      style={{
                        background: "#fde8e8",
                        border: "none",
                        color: "#e74c3c",
                        cursor: "pointer",
                        padding: "4px 10px",
                        // borderRadius: "6px",
                        fontSize: "14px",
                        fontWeight: "600",
                        transition: "all 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = "#f8d7da";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = "#fde8e8";
                      }}
                    >
                      Remove
                    </button>
                  </div>
                )}

                {attachmentFile && (
                  <input
                    type="text"
                    placeholder="Attachment description (optional)"
                    value={attachmentDescription}
                    onChange={(e) => setAttachmentDescription(e.target.value)}
                    style={{
                      width: "100%",
                      marginTop: "8px",
                      padding: "8px 12px",
                      // borderRadius: "8px",
                      border: "2px solid #e0e0e0",
                      fontSize: "clamp(13px, 1vw, 14px)",
                      outline: "none",
                      transition: "all 0.3s ease",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#F9A826";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e0e0e0";
                    }}
                  />
                )}
              </div>

              <button
                onClick={handleUpdateStatus}
                disabled={updating || uploading}
                style={{
                  width: "100%",
                  padding: "14px",
                  background:
                    updating || uploading
                      ? "linear-gradient(135deg, #95a5a6, #7f8c8d)"
                      : "linear-gradient(135deg, #F9A826, #f5a623)",
                  color: "white",
                  border: "none",
                  // borderRadius: "10px",
                  fontSize: "clamp(15px, 1.2vw, 17px)",
                  fontWeight: "700",
                  cursor: updating || uploading ? "not-allowed" : "pointer",
                  transition: "all 0.3s ease",
                  opacity: updating || uploading ? 0.7 : 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
                onMouseEnter={(e) => {
                  if (!updating && !uploading) {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 25px rgba(249,168,38,0.4)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                {uploading ? (
                  <>
                    <span
                      style={{
                        display: "inline-block",
                        width: "18px",
                        height: "18px",
                        border: "3px solid rgba(255,255,255,0.3)",
                        borderTop: "3px solid #fff",
                        // borderRadius: "50%",
                        animation: "spin 0.8s linear infinite",
                      }}
                    />
                    Uploading File...
                  </>
                ) : updating ? (
                  <>
                    <span
                      style={{
                        display: "inline-block",
                        width: "18px",
                        height: "18px",
                        border: "3px solid rgba(255,255,255,0.3)",
                        borderTop: "3px solid #fff",
                        // borderRadius: "50%",
                        animation: "spin 0.8s linear infinite",
                      }}
                    />
                    Updating...
                  </>
                ) : (
                  "Update Complaint"
                )}
              </button>

              <div
                style={{
                  marginTop: "16px",
                  paddingTop: "16px",
                  borderTop: "1px solid #eee",
                  textAlign: "center",
                }}
              >
                <p
                  style={{
                    fontSize: "clamp(11px, 0.9vw, 13px)",
                    color: "#999",
                    margin: 0,
                  }}
                >
                  <span style={{ opacity: 0.5 }}>#</span>
                  {complaint.trackingId || complaint._id?.slice(-6)}
                </p>
                <p
                  style={{
                    fontSize: "clamp(10px, 0.8vw, 12px)",
                    color: "#bbb",
                    margin: "4px 0 0 0",
                  }}
                >
                  Created {formatDate(complaint.createdAt).split(",")[0]}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(30px, -30px) scale(1.1); }
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @media (max-width: 1024px) {
          .complaint-detail-grid { grid-template-columns: 1fr !important; }
          .complaint-actions-sticky { position: relative !important; top: 0 !important; }
        }
        @media (max-width: 768px) {
          .complaint-header { flex-direction: column !important; align-items: flex-start !important; }
          .complaint-info-grid { grid-template-columns: 1fr !important; }
          textarea, select { font-size: 16px !important; }
        }
      `}</style>
    </div>
  );
};

export default ComplaintDetail;
